import { Link } from '@tanstack/react-router';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef, type ReactNode } from 'react';
import { t } from '../../i18n';
import { ErrorState } from '../feedback/ErrorState';
import { useScrolls } from '../lib/use-scrolls';
import { cn } from '../lib/utils';
import { Button } from '../primitives/button';
import type { DataError } from '../types';
import { LoadingRows } from './LoadingRows';
import { useLoadMoreAtEnd } from './use-load-more';

type ListProps<T> = {
  label: string;
  items: T[] | undefined;
  getKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  /** grid: tiles side by side (the content grid). */
  layout?: 'list' | 'grid';
  /** A row's estimated height in px, until the row is measured. */
  estimateSize?: number;
  loading?: boolean;
  skeletonCount?: number;
  error?: DataError | null;
  empty?: ReactNode;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  /** In place of the catalog's Daha fazla and Yükleniyor…, for an app whose chrome is not Turkish. */
  labels?: { loadMore?: string; loading?: string };
};

// A longer list renders only the rows in view, in its own scroll area (design system §4 List).
const virtualAbove = 50;

export function List<T>({
  label,
  items,
  getKey,
  renderItem,
  layout = 'list',
  estimateSize = 72,
  loading = false,
  skeletonCount = 6,
  error,
  empty,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  labels,
}: ListProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const count = items?.length ?? 0;
  // A grid's rows hold several tiles, so a grid is never virtualised.
  const virtual = layout === 'list' && count > virtualAbove;
  const virtualizer = useVirtualizer({
    count: virtual ? count : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateSize,
  });
  const inView = virtualizer.getVirtualItems();
  useLoadMoreAtEnd(inView.at(-1)?.index, count, { hasMore, loadingMore, onLoadMore });
  // Null while the box is not shown, so the box observed is the one in place.
  const scrolls = useScrolls(scrollRef, loading || error ? null : items);

  if (loading) return <LoadingRows count={skeletonCount} label={labels?.loading} />;
  if (error) return <ErrorState {...error} />;
  if (!items?.length) return <>{empty}</>;

  const indexes = virtual ? inView.map((item) => item.index) : items.map((_, index) => index);
  // Padding stands in for the rows out of view, so the scroll height stays that of the whole list.
  const before = virtual ? (inView[0]?.start ?? 0) : 0;
  const after = virtual ? virtualizer.getTotalSize() - (inView.at(-1)?.end ?? 0) : 0;
  return (
    <div
      ref={scrollRef}
      // While its content overflows, the box is a region a keyboard can focus and scroll (WS-16 finding 13).
      {...(scrolls && { role: 'region', 'aria-label': label, tabIndex: 0 })}
      className="flex max-h-dvh flex-col overflow-y-auto focus-ring"
    >
      <ul
        aria-label={label}
        className={
          layout === 'grid'
            ? 'grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3'
            : 'divide-y divide-border'
        }
        // Only while virtual, so that a grid keeps the padding of its class.
        style={virtual ? { paddingTop: before, paddingBottom: after } : undefined}
      >
        {indexes.map((index) => {
          const item = items[index]!;
          return (
            <li
              key={getKey(item)}
              // Measured, so a row shorter or taller than the estimate leaves no gap and no overlap. The ref changes
              // when the list turns virtual, so the rows already shown are measured then too.
              ref={virtual ? virtualizer.measureElement : undefined}
              data-index={virtual ? index : undefined}
              aria-setsize={virtual ? count : undefined}
              aria-posinset={virtual ? index + 1 : undefined}
            >
              {renderItem(item)}
            </li>
          );
        })}
      </ul>
      {hasMore && (
        <Button
          variant="outline"
          className="m-4 self-center"
          disabled={loadingMore}
          onClick={onLoadMore}
        >
          {labels?.loadMore ?? t('components.List.loadMore')}
        </Button>
      )}
    </div>
  );
}

type ListItemProps = {
  title: string;
  href?: string;
  titleAside?: ReactNode;
  description?: ReactNode;
  leading?: ReactNode;
  meta?: ReactNode;
  trailing?: ReactNode;
  unread?: boolean;
  selected?: boolean;
};

// A row whose title is its one link, stretched over the row; the trailing actions sit above that link, never inside
// it (merchant screens §3.3, §3.4).
export function ListItem({
  title,
  href,
  titleAside,
  description,
  leading,
  meta,
  trailing,
  unread = false,
  selected = false,
}: ListItemProps) {
  const titleClass = unread ? 'font-semibold' : 'font-medium';
  const titleText = (
    <>
      {title}
      {unread && <span className="sr-only">{t('components.ListItem.unread')}</span>}
    </>
  );
  return (
    <div
      aria-current={selected || undefined}
      className={cn(
        'relative flex items-start gap-3 p-4',
        selected ? 'bg-accent-muted' : 'hover:bg-surface-muted',
      )}
    >
      {leading}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          {unread && <span aria-hidden className="size-2 shrink-0 rounded-full bg-accent" />}
          {href ? (
            <Link
              to={href}
              className={cn(titleClass, 'rounded-sm focus-ring after:absolute after:inset-0')}
            >
              {titleText}
            </Link>
          ) : (
            <span className={titleClass}>{titleText}</span>
          )}
          {titleAside && <span className="ms-auto text-xs text-muted">{titleAside}</span>}
        </div>
        {description && <div className="text-sm text-muted">{description}</div>}
        {meta && <div className="flex flex-wrap items-center gap-2">{meta}</div>}
      </div>
      {trailing && <div className="relative flex items-center gap-2">{trailing}</div>}
    </div>
  );
}
